@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.allFromTabularCommit
import org.example.project.enginekartei.KarteiApi
import org.example.project.service.InfrastrukturApi
import org.example.project.service.GlobalManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

@Composable
fun CUSTorAuskunft(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var daten by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var selectedHalle by remember { mutableStateOf("") }
    var strukturHallen by remember { mutableStateOf<List<String>>(emptyList()) }
    var strukturTore by remember { mutableStateOf<List<String>>(emptyList()) }
    var torVon by remember { mutableStateOf("") }
    var torBis by remember { mutableStateOf("") }
    var infra by remember { mutableStateOf<All?>(null) }
    var saveAuftrag by remember { mutableStateOf<List<Pair<String, List<String>>>?>(null) }
    var deleteAuftrag by remember { mutableStateOf<List<String>?>(null) }
    var generateTore by remember { mutableStateOf<List<All>>(emptyList()) }
    var showGenerateConfirm by remember { mutableStateOf(false) }
    var generateBusy by remember { mutableStateOf(false) }
    var generateProgress by remember { mutableStateOf(0) }
    var generateResultText by remember { mutableStateOf("") }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("yard-kontrollen")
    )

    suspend fun loadTore() {
        daten = letzteFilter?.let { InfrastrukturApi.searchTore(it) }.orEmpty()
            .sortedWith(
                compareBy<All>(
                    { it.standort.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.halle.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.tor ?: Int.MAX_VALUE }
                )
            )
    }

    LaunchedEffect(art, refreshVersion) {
        infra = InfrastrukturApi.loadInfra()
        standorte = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: 0 }
        loadTore()
    }

    LaunchedEffect(standorte) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standorte.map { it.id }) {
            selectedStandort = ""
        }
    }

    LaunchedEffect(selectedStandort, standorte) {
        val standort = standorte.firstOrNull { it.id == selectedStandort }
        strukturHallen = eingebetteterStandortGeneratorRange(standort?.halleVon.orEmpty(), standort?.halleBis.orEmpty(), 2)
        strukturTore = eingebetteterStandortGeneratorRange(standort?.toreVon.orEmpty(), standort?.toreBis.orEmpty(), 3)
        selectedHalle = ""
        torVon = ""
        torBis = ""
    }

    val headers = listOf("Standort", "Halle", "Tor", "Torcode", "Belegt", "Gesperrt")
    val saveFields = listOf("standort", "halle", "tor", "torcode", "belegt")

    fun generateTorcode(item: All): String =
        infraCodeAusMeta(
            infra = infra,
            typ = "tor",
            values = mapOf(
                "standort" to item.standort,
                "halle" to item.halle,
                "tor" to (item.tor ?: 0).toString()
            )
        )

    fun torRowId(item: All): String =
        item.torcode.orEmpty()

    val standortOptionen = buildList {
        add(All(key = "", label = "Standort"))
        addAll(standorte.map { s ->
            All(key = s.id, label = s.standort.ifBlank { "Standort ${s.id}" })
        })
    }
    val sichtbareTore = daten

    LaunchedEffect(saveAuftrag) {
        val rows = saveAuftrag ?: return@LaunchedEffect
        val toDelete = mutableListOf<All>()
        val toSave = mutableListOf<All>()
        rows.forEach { (id, cols) ->
            val existing = daten.firstOrNull { torRowId(it) == id }
            val updated = allFromTabularCommit(
                rowId = id,
                cols = listOf(
                    cols.getOrElse(0) { existing?.standort.orEmpty() },
                    cols.getOrElse(1) { existing?.halle.orEmpty() },
                    cols.getOrElse(2) { existing?.tor?.toString() ?: "0" },
                    cols.getOrElse(3) { existing?.torcode.orEmpty() },
                    cols.getOrElse(4) { if (existing?.belegt == true) "1" else "0" }
                ),
                fieldIds = saveFields
            )
            val torcode = updated.torcode.orEmpty().ifBlank { generateTorcode(updated) }
            if (torcode.isBlank()) return@forEach
            val final = updated.copy(
                id = torcode,
                torcode = torcode,
                gesperrt = cols.getOrElse(5) { if (existing?.gesperrt == true) "✅" else "⬜" } == "✅"
            )
            if (existing == null || existing != final) {
                if (existing != null && torRowId(existing) != torRowId(final)) {
                    toDelete += All(id = torRowId(existing), torcode = torRowId(existing))
                }
                toSave += final
            }
        }
        InfrastrukturApi.deleteTore(toDelete)
        InfrastrukturApi.saveTore(toSave)
        saveAuftrag = null
        loadTore()
    }

    LaunchedEffect(deleteAuftrag) {
        val ids = deleteAuftrag ?: return@LaunchedEffect
        InfrastrukturApi.deleteTore(ids.map { rowId -> All(id = rowId, torcode = rowId) })
        deleteAuftrag = null
        loadTore()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            padding(12.px)
            height(100.percent)
            overflow("hidden")
            property("box-sizing", "border-box")
            property("min-height", "0")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
            }
        }) {
            Div({ style { width(240.px) } }) {
                AppDropdown(
                    options = standortOptionen,
                    selectedValue = selectedStandort,
                    onChange = {
                        selectedStandort = it
                    },
                    appearance = AppDropdownAppearance.Field,
                    menuMode = AppDropdownMenuMode.Overlay
                )
            }
            StandortGeneratorDropdown(
                label = "Halle",
                selectedValue = selectedHalle,
                options = strukturHallen,
                enabled = selectedStandort.isNotBlank() && strukturHallen.isNotEmpty(),
                onChange = { selectedHalle = it }
            )
            StandortGeneratorDropdown(
                label = "Tor von",
                selectedValue = torVon,
                options = strukturTore,
                enabled = selectedHalle.isNotBlank() && strukturTore.isNotEmpty(),
                onChange = { torVon = it }
            )
            StandortGeneratorDropdown(
                label = "Tor bis",
                selectedValue = torBis,
                options = strukturTore,
                enabled = selectedHalle.isNotBlank() && strukturTore.isNotEmpty(),
                onChange = { torBis = it }
            )
            Div({ style { width(150.px) } }) {
                LumaticaButton(
                    label = "Reset",
                    onClick = {
                        selectedStandort = ""
                        selectedHalle = ""
                        torVon = ""
                        torBis = ""
                    },
                    variant = LumaticaButtonVariant.Standard
                )
            }
            Div({ style { width(150.px) } }) {
                LumaticaButton(
                    label = "Generieren",
                    onClick = {
                        scope.launch {
                            val standortId = selectedStandort.takeIf { it.isNotBlank() } ?: return@launch
                            val hallenId = selectedHalle.takeIf { it.isNotBlank() } ?: return@launch
                            val von = torVon.toIntOrNull() ?: return@launch
                            val bis = torBis.toIntOrNull() ?: return@launch
                            val created = (von..bis).mapNotNull { torNr ->
                                val torcode = infraCodeAusMeta(
                                    infra = infra,
                                    typ = "tor",
                                    values = mapOf(
                                        "standort" to standortId,
                                        "halle" to hallenId,
                                        "tor" to torNr.toString()
                                    )
                                )
                                if (torcode.isBlank()) null else All(
                                    id = torcode,
                                    torcode = torcode,
                                    standort = standortId,
                                    halle = hallenId,
                                    tor = torNr,
                                    belegt = false,
                                    gesperrt = false
                                )
                            }
                            if (created.isNotEmpty()) {
                                generateTore = created
                                showGenerateConfirm = true
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Action
                )
            }
        }

        Div({
            style {
                property("flex", "1 1 auto")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = "TorAuskunft",
                rollenKey = "TorAuskunft",
                title = "Torauskunft",
                headers = headers,
                models = daten.map { it.copy(id = torRowId(it)) },
                data = sichtbareTore.map { tor ->
                    torRowId(tor) to listOf(
                        tor.standort,
                        tor.halle,
                        tor.tor?.toString().orEmpty(),
                        tor.torcode.orEmpty(),
                        if (tor.belegt) "1" else "0",
                        if (tor.gesperrt == true) "✅" else "⬜"
                    )
                },
                editable = true,
                includeMetadata = true,
                nonEditableCols = setOf("Torcode"),
                toggleColumns = setOf("Gesperrt"),
                allowCreate = false,
                allowRefreshButton = false,
                onSearch = { scope.launch { loadTore() } },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        letzteFilter = filters
                        daten = InfrastrukturApi.searchTore(filters)
                    }
                },
                onCommit = { rows ->
                    saveAuftrag = rows
                },
                onDeleteRows = { ids ->
                    val deletableIds = ids.filter { rowId ->
                        daten.firstOrNull { torRowId(it) == rowId }?.belegt != true
                    }
                    if (deletableIds.size != ids.size) {
                        error("Belegte Tore können nicht gelöscht werden.")
                    }
                    deleteAuftrag = deletableIds
                }
            )
        }
        if (showGenerateConfirm) {
            GeneratorConfirmPopup(
                count = generateTore.size,
                itemLabel = "Tore",
                onCancel = {
                    showGenerateConfirm = false
                    generateTore = emptyList()
                },
                onConfirm = {
                    val items = generateTore
                    showGenerateConfirm = false
                    generateBusy = true
                    generateProgress = 8
                    scope.launch {
                        delay(120)
                        generateProgress = 34
                        GlobalManager.markLocalScreenWrite("TorAuskunft", suppressMs = 6000L)
                        val saved = InfrastrukturApi.saveTore(items)
                        generateProgress = 76
                        loadTore()
                        generateProgress = 100
                        delay(240)
                        generateTore = emptyList()
                        generateResultText = "Es wurden ${saved.size} Tore erzeugt."
                    }
                }
            )
        }
        if (generateBusy) {
            GeneratorProgressPopup(
                count = generateTore.size,
                itemLabel = "Tore",
                progress = generateProgress,
                resultMessage = generateResultText,
                onClose = {
                    generateBusy = false
                    generateResultText = ""
                }
            )
        }
    }
}
